import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import axios from 'axios'
import saveAs from 'file-saver'
import { useMutation } from '@tanstack/react-query'
import { reverseUrl, ViewNames } from '../../../../navigation/reverseUrl'
import { ItemType } from '../../Documents/item-type'
import { IBinderModel } from '../../../../interfaces/models/binder.interfaces'
import { IconChoices } from '../../../common/Icon'
import { openUrl, parseUrlWhitespaces } from '../../../../helpers/url'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { BinderListRowItem } from './BinderListRowItem'
import { BinderListCardItem } from './BinderListCardItem'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { useModal } from '../../../../hooks/useModal'
import { deleteDocument } from '../../../../api/binder'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { Option } from '../../../common/Dropdown'
import { CreateUpdateBinderModal } from '../CreateUpdateBinderModal'
import { CreateUpdateBinderDocumentModal } from '../CreateUpdateBinderDocumentModal'
import { useDocumentDroppable } from '../../Documents/DocumentsListItem/useDocumentDroppable'

interface DocumentsListItemProps {
  document: IBinderModel
  binder: IBinderModel[]
  documentRoute: ViewNames
  isListMode: boolean
  isAdminView: boolean
  unitId?: string
  binderId: string
}

export const BinderListItem = ({
  document,
  binder,
  documentRoute,
  isListMode,
  unitId,
  isAdminView,
  binderId,
}: DocumentsListItemProps): React.ReactNode => {
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const navigate = useNavigate()

  const { parentId: docParentId } = document
  const parentId = docParentId || undefined

  const getType = (): ItemType => {
    switch (document.type) {
      case 'folder':
        return ItemType.FOLDER
      default:
        return ItemType.DOCUMENT
    }
  }

  const documentType = getType()
  const [{ isDragging }, drag, dragPreview] = useDrag(
    () => ({
      type: documentType,
      item: { ...document, isListMode },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: isAdminView,
    }),
    [document, documentType, isAdminView, isListMode]
  )

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true })
  }, [dragPreview])

  const icon =
    documentType === ItemType.FOLDER ? IconChoices.FOLDER : IconChoices.DOCUMENT

  const isFolder = documentType === ItemType.FOLDER
  const { isOver, drop } = useDocumentDroppable(
    document._id,
    'binder',
    isAdminView && isFolder
  )

  const { mutate: mutateDeleteDocument } = useMutation({
    mutationFn: async (params: { documentId: string }) => {
      await deleteDocument(params.documentId)
    },
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate(
          isFolder
            ? 'createUpdateFolder.flashMessage.deleteFolderSuccess'
            : 'createUpdateDocument.flashMessage.deleteDocumentSuccess'
        )
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate(
          isFolder
            ? 'createUpdateFolder.flashMessage.deleteFolderFailure'
            : 'createUpdateDocument.flashMessage.deleteDocumentFailure'
        )
      )
    },
  })

  const {
    show: showEditFolderModal,
    open: openEditFolderModal,
    close: closeEditFolderModal,
  } = useModal()
  const {
    show: showEditDocumentModal,
    open: openEditDocumentModal,
    close: closeEditDocumentModal,
  } = useModal()
  const {
    show: showRemoveConfirmationModal,
    open: openRemoveConfirmationModal,
    close: closeRemoveConfirmationModal,
  } = useModal()

  const openFolder = (): void => {
    if (isFolder) {
      navigate(
        reverseUrl(documentRoute, {
          id: society?._id,
          unitId,
          documentId: document._id,
        })
      )
    }
  }

  const openFile = (): void => {
    if (document.url) {
      openUrl(parseUrlWhitespaces(`${document.url}`))
    }
  }

  const openItem = (): void => {
    if (isFolder) {
      openFolder()
    }
    if (!isFolder) {
      openFile()
    }
  }

  const downloadDocument = (): void => {
    if (!document || !document.url) return

    axios
      .get(document.url, { responseType: 'blob', withCredentials: false })
      .then((response) => {
        saveAs(new Blob([response.data]), document.name)
      })
  }

  const createDropdownOptions = (): Option[] => {
    const options = []

    if (!isFolder) {
      options.push({
        value: 'download',
        label: translate('common.actions.download'),
        onClick: async () => downloadDocument(),
      })
    }
    if (isAdminView) {
      options.push({
        value: 'edit',
        label: translate('common.actions.edit'),
        onClick: () =>
          isFolder ? openEditFolderModal() : openEditDocumentModal(),
      })
      options.push({
        value: 'remove',
        label: translate('common.actions.delete'),
        onClick: async () => openRemoveConfirmationModal(),
        destructive: true,
      })
    }

    return options
  }
  const dropdownOptions = createDropdownOptions()

  const getDocumentSubTitleText = (): string => {
    const childDocuments = binder.filter((doc) => doc.parentId === document._id)

    const numFolders = childDocuments.filter(
      (_document) => _document.type === 'folder'
    ).length
    const numFiles = childDocuments.length - numFolders

    const items = []
    if (numFolders > 0) {
      items.push(
        `${numFolders} ${
          numFolders === 1
            ? translate('documentsList.document.folderSingular')
            : translate('documentsList.document.folderPlural')
        }`
      )
    }
    if (numFiles > 0) {
      items.push(
        `${numFiles} ${
          numFolders === 1
            ? translate('documentsList.document.documentSingular')
            : translate('documentsList.document.documentPlural')
        }`
      )
    }

    return items.join(', ')
  }

  const documentSubtitle = getDocumentSubTitleText()

  const removeFolder = (): void => {
    mutateDeleteDocument({ documentId: document._id })
  }

  return (
    <>
      <div ref={isFolder ? drop : null} role={isFolder ? 'drop' : undefined}>
        {isListMode ? (
          <BinderListRowItem
            isAdminView={isAdminView}
            openItem={openItem}
            documentTitle={document.name || ''}
            documentSubtitle={documentSubtitle}
            documentIcon={icon}
            isFolder={isFolder}
            documentUrl={document.thumbnail}
            dropdownOptions={dropdownOptions}
            isDragging={isDragging}
            drag={drag}
            isOver={isOver}
          />
        ) : (
          <BinderListCardItem
            isAdminView={isAdminView}
            openItem={openItem}
            documentTitle={document.name || ''}
            documentSubtitle={documentSubtitle}
            documentIcon={icon}
            isFolder={isFolder}
            documentUrl={document.thumbnail}
            dropdownOptions={dropdownOptions}
            isDragging={isDragging}
            drag={drag}
            isOver={isOver}
          />
        )}
      </div>
      <CreateUpdateBinderModal
        show={showEditFolderModal}
        close={closeEditFolderModal}
        id={document._id}
        parentId={parentId}
        binderId={binderId}
      />

      <CreateUpdateBinderDocumentModal
        show={showEditDocumentModal}
        close={closeEditDocumentModal}
        parentId={parentId}
        id={document._id}
        binderId={binderId}
      />
      <ConfirmationModal
        title={translate(
          isFolder
            ? 'createUpdateFolder.removeFolderConfirmationModal.title'
            : 'createUpdateDocument.removeDocumentConfirmationModal.title'
        )}
        description={translate(
          isFolder
            ? 'createUpdateFolder.removeFolderConfirmationModal.description'
            : 'createUpdateDocument.removeDocumentConfirmationModal.description'
        )}
        show={showRemoveConfirmationModal}
        close={closeRemoveConfirmationModal}
        onConfirm={removeFolder}
        deleteMode
      />
    </>
  )
}
